import { pathwayLogo } from '@/assets/images';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { WEB_SMART_APP_BANNER_LAYOUT_EVENT, createStyles, useScaling } from '@/hooks';
import { envPublic } from '@/libs/config';
import { colors, guardianColors } from '@/theme/colors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Image, Linking, Platform, Pressable, View, type LayoutChangeEvent } from 'react-native';

const IOS_APP_STORE_FALLBACK =
  'https://apps.apple.com/us/app/u-s-kids-golf-player-pathway/id1493477986';
const ANDROID_PLAY_STORE_FALLBACK =
  'https://play.google.com/store/apps/details?id=com.uskg.playerpathway&hl=en';
const BANNER_BACKGROUND_COLOR = guardianColors['2'];
const CTA_BACKGROUND_COLOR = guardianColors.Blue;
const TEXT_SECONDARY_COLOR = guardianColors['6'];

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    platform?: string;
  };
};

function getMobilePlatform(): 'ios' | 'android' | null {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') return null;

  const nav = navigator as NavigatorWithUserAgentData;
  const userAgent = nav.userAgent || '';
  const userAgentPlatform = nav.userAgentData?.platform || '';
  const platform = nav.platform || '';
  const platformHint = `${userAgentPlatform} ${platform}`;

  if (/android/i.test(userAgent) || /android/i.test(platformHint)) return 'android';
  if (/iPad|iPhone|iPod/.test(userAgent) || /iPad|iPhone|iPod/.test(platformHint)) return 'ios';

  // Chrome on Android tablets can request the desktop site and report an X11 Linux UA.
  // The platform still exposes Linux ARM with touch support, which distinguishes it
  // from a regular desktop browser for our banner use case.
  if (/linux/i.test(platformHint) && /arm/i.test(platformHint) && (nav.maxTouchPoints ?? 0) > 1) {
    return 'android';
  }

  // iPadOS desktop-mode Safari reports as Mac; touch support separates it from desktop Safari.
  if (
    (/Macintosh|MacIntel|MacPPC|Mac68K/.test(userAgent) ||
      /MacIntel|MacPPC|Mac68K/.test(platformHint)) &&
    typeof nav.maxTouchPoints === 'number'
  ) {
    return nav.maxTouchPoints > 1 ? 'ios' : null;
  }

  return null;
}

function getStoreUrl(platform: 'ios' | 'android'): string {
  if (platform === 'ios') {
    return envPublic.iosAppStoreUrl || IOS_APP_STORE_FALLBACK;
  }

  return envPublic.androidPlayStoreUrl || ANDROID_PLAY_STORE_FALLBACK;
}

export function SmartAppBanner() {
  const styles = useStyles();
  const { scale } = useScaling();
  const [dismissed, setDismissed] = useState(false);
  const platform = useMemo(getMobilePlatform, []);
  const shouldRender = Platform.OS === 'web' && platform && !dismissed;

  const publishBannerHeight = useCallback((height: number) => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    window.__SMART_APP_BANNER_HEIGHT__ = Math.max(0, height);
    window.dispatchEvent(new Event(WEB_SMART_APP_BANNER_LAYOUT_EVENT));
  }, []);

  useEffect(() => {
    if (!shouldRender) {
      publishBannerHeight(0);
    }

    return () => publishBannerHeight(0);
  }, [publishBannerHeight, shouldRender]);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      publishBannerHeight(event.nativeEvent.layout.height);
    },
    [publishBannerHeight],
  );

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const handleOpen = useCallback(() => {
    if (!platform) return;

    Linking.openURL(getStoreUrl(platform));
  }, [platform]);

  if (!shouldRender) return null;

  return (
    <View nativeID="smart-app-banner" onLayout={handleLayout} style={styles.root}>
      <Pressable
        accessibilityLabel={STRINGS.smartAppBanner.dismissAccessibilityLabel}
        accessibilityRole="button"
        hitSlop={scale(8)}
        onPress={handleDismiss}
        style={styles.closeButton}>
        <SvgIcon name="IcXClose" size={scale(14)} color={colors['6']} />
      </Pressable>

      <View style={styles.logoTile}>
        <Image source={pathwayLogo} resizeMode="contain" style={styles.logo} />
      </View>

      <View style={styles.copy}>
        <Text
          numberOfLines={1}
          fontFamily="OpenSans"
          fontSize={scale(12)}
          fontWeight="600"
          lineHeight={scale(17)}
          style={styles.title}>
          {STRINGS.smartAppBanner.title}
        </Text>
        <Text
          numberOfLines={1}
          fontFamily="OpenSans"
          fontSize={scale(10)}
          fontWeight="400"
          lineHeight={scale(12)}
          style={styles.subtitle}>
          {STRINGS.smartAppBanner.publisher}
        </Text>
        <Text
          numberOfLines={1}
          fontFamily="OpenSans"
          fontSize={scale(10)}
          fontWeight="400"
          lineHeight={scale(12)}
          style={styles.caption}>
          {STRINGS.smartAppBanner.caption}
        </Text>
      </View>

      <Pressable
        accessibilityLabel={STRINGS.smartAppBanner.openAccessibilityLabel}
        accessibilityRole="button"
        onPress={handleOpen}
        style={({ pressed }) => [styles.actionButton, pressed && styles.actionButtonPressed]}>
        <Text
          fontFamily="OpenSans"
          fontSize={scale(12)}
          fontWeight="600"
          lineHeight={scale(20)}
          style={styles.actionText}>
          {STRINGS.smartAppBanner.get}
        </Text>
      </Pressable>
    </View>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    height: scale(68),
    width: '100%',
    backgroundColor: BANNER_BACKGROUND_COLOR,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: scale(12),
    paddingVertical: scale(4),
    gap: scale(12),
    zIndex: 10,
  },
  closeButton: {
    width: scale(16),
    height: scale(16),
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoTile: {
    width: scale(48),
    height: scale(48),
    borderRadius: scale(12),
    backgroundColor: colors['0'],
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  logo: {
    width: '100%',
    height: '100%',
  },
  copy: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  title: {
    color: colors['Grays/Black'],
  },
  subtitle: {
    color: TEXT_SECONDARY_COLOR,
  },
  caption: {
    color: TEXT_SECONDARY_COLOR,
  },
  actionButton: {
    minWidth: scale(56),
    height: scale(32),
    borderRadius: scale(16),
    backgroundColor: CTA_BACKGROUND_COLOR,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: scale(16),
  },
  actionButtonPressed: {
    opacity: 0.72,
  },
  actionText: {
    color: colors['0'],
  },
}));
